import { useEffect, useRef, useCallback, useState } from 'react';
import { useEditor } from '@tiptap/react';
import { DOMParser as PMDOMParser, Slice } from 'prosemirror-model';
import { TextSelection, Selection } from 'prosemirror-state';
import extensions from '../../tiptap/extensions';
import { cleanSlice } from '../../helpers/contentHelpers';
import * as formatHelpers from '../../helpers/formatHelpers';
import { isModifiedPasteShortcutEvent } from '../../helpers/pasteHelpers';
import { notifyToast } from '../../helpers/notificationHelpers';
import {
  EMPTY_LINE_SUGGESTION_CHAR,
  PARAGRAPH_BREAK_SUGGESTION_CHAR,
  HARD_BREAK_SUGGESTION_CHAR,
  isStructuralSuggestionMarkerChar,
  createSuggestionMarkAttrs,
  normalizeExistingSuggestionAttrs,
  findAdjacentSuggestionAttrs,
  rangeContainsOnlyAddSuggestionText,
  rangeIntersectsSuggestionMark,
  getTextOverlapRanges,
  shouldUseNativePasteBypass,
  getFallbackPasteText,
  shouldHandleStructuralEmptyDelete,
  shouldHandleStructuralHardBreakDelete,
  getStructuralHardBreakMarkerPos,
  shouldHandleStructuralSuggestEnter,
  shouldInjectListItemSuggestionMarker,
  getSingleAddSuggestionPlaceholderRange,
  getRemovableSuggestedListItemRange,
  getRemovableSuggestedListItemRangeForContentDeletion,
  shouldBlockBoundaryDeleteOnRemoveSuggestedListItem,
  getTableSuggestionContext,
  clearSuggestionMarksInRange
} from '../../helpers/suggestionHelpers';
import '../../styles/document.css';
import PopoverLinks from '../../components/Overlays/PopoverLinks';

// Configurable save timeout in milliseconds
const SAVE_TIMEOUT_MS = 0;
// How long after clicking the editor area the view-only toast can fire (ms)
const EDITOR_CLICK_WINDOW_MS = 10000;
// Minimum interval between successive view-only toasts (ms)
const VIEW_ONLY_TOAST_INTERVAL_MS = 3000;
// How long a modified-paste shortcut intent is considered valid before paste fires.
const MODIFIED_PASTE_SHORTCUT_WINDOW_MS = 1200;

function isCodePasteContext(view) {
  const state = view?.state;
  const selection = state?.selection;
  if (!selection?.$from || !selection?.$to) return false;

  const hasCodeNodeInPath = ($pos) => {
    if (!$pos) return false;
    for (let depth = $pos.depth; depth >= 0; depth -= 1) {
      const node = $pos.node(depth);
      if (!node?.type) continue;
      if (node.type.spec?.code) return true;
      if (node.type.name === 'codeBlock') return true;
    }
    return false;
  };

  if (hasCodeNodeInPath(selection.$from) || hasCodeNodeInPath(selection.$to)) {
    return true;
  }

  const codeMark = state?.schema?.marks?.code;
  if (!codeMark) return false;
  return Boolean(codeMark.isInSet(selection.$from.marks()) || codeMark.isInSet(selection.$to.marks()));
}

const normalizeDiffMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'suggest' ? 'suggest' : 'write';
};
export default function RichEditor({ content, onChange, onUserInteraction = null, setEditorInstance, htmlMode, onBlur, readOnly = false, projectId = null, itemsMap = null, onOpenProjectDoc = null, docId = null, pane = null, diffMode = 'write', currentUserId = null }) {
  const lastSaveTime = useRef(0);
  const saveTimeoutRef = useRef(null);
  const isSystemUpdate = useRef(false);
  const rootRef = useRef(null);
  // Keep a live ref to readOnly so event handlers set up once always see
  // the current value without stale closures.
  const readOnlyRef = useRef(readOnly);
  useEffect(() => { readOnlyRef.current = readOnly; }, [readOnly]);
  // Throttle the toast so repeated keypresses don't spam the UI.
  const viewOnlyToastThrottleRef = useRef(0);
  // Tracks the last time the user pressed the mouse button inside the editor
  // area. Used to scope the window keydown listener to the editor context.
  const lastEditorClickRef = useRef(0);
  // Stable ref to editor commands so handlePaste can use them after editor init.
  const editorCommandRef = useRef(null);
  // Shift+paste is treated as an explicit opt-out from structural paste formatting.
  const bypassStructuredPasteFormattingRef = useRef(false);
  // Tracks whether the current paste target is a code block / code-marked context.
  const codePasteContextRef = useRef(false);
  // Some browsers do not reliably expose shiftKey on paste events for Ctrl+Shift+V.
  const lastModifiedPasteShortcutAtRef = useRef(0);
  const diffModeRef = useRef(normalizeDiffMode(diffMode));
  const suggestTypingBatchRef = useRef({ batchId: null, annotationId: null, lastAt: 0, lastCursor: null });
  const suggestDeleteBatchRef = useRef({ batchId: null, annotationId: null, lastAt: 0, lastCursor: null, key: null });
  const pendingSuggestListEnterRef = useRef(null);

  useEffect(() => {
    diffModeRef.current = normalizeDiffMode(diffMode);
    suggestTypingBatchRef.current = { batchId: null, annotationId: null, lastAt: 0, lastCursor: null };
    suggestDeleteBatchRef.current = { batchId: null, annotationId: null, lastAt: 0, lastCursor: null, key: null };
    pendingSuggestListEnterRef.current = null;
  }, [diffMode]);

  const notifyUserInteraction = useCallback((event = null, interactionType = 'unknown') => {
    if (typeof onUserInteraction !== 'function') return;
    if (readOnlyRef.current) return;
    const key = String(event?.key || '').trim();
    const hasModifier = Boolean(event?.metaKey || event?.ctrlKey || event?.altKey);
    onUserInteraction({
      at: Date.now(),
      type: interactionType,
      key: key || null,
      hasModifier
    });
  }, [onUserInteraction]);

  const debouncedOnChange = useCallback(
    ({ json, html }) => {
      const now = Date.now();
      lastSaveTime.current = now;
      onChange({ json, html });
    },
    [onChange]
  );

  const trySmartBoundaryPaste = useCallback((view, slice) => {
    if (!view?.state || !slice?.content) return false;
    const { state } = view;
    const { selection } = state;
    if (!selection || !selection.empty) return false;

    let hasBlockNode = false;
    try {
      for (let i = 0; i < slice.content.childCount; i += 1) {
        const node = slice.content.child(i);
        if (node?.isBlock) {
          hasBlockNode = true;
          break;
        }
      }
    } catch (err) {
      return false;
    }
    if (!hasBlockNode) return false;

    const $from = selection.$from;
    if (!$from?.parent?.isTextblock) return false;

    const atStart = $from.parentOffset === 0;
    const atEnd = $from.parentOffset === $from.parent.content.size;
    if (!atStart && !atEnd) return false;

    const depth = $from.depth;
    if (depth < 1) return false;

    const insertPos = atStart ? $from.before(depth) : $from.after(depth);
    if (!Number.isFinite(insertPos)) return false;

    try {
      const tr = state.tr.replaceRange(insertPos, insertPos, slice).scrollIntoView();
      view.dispatch(tr);
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  const editor = useEditor({
    extensions,
    content,
    autofocus: false,
    editable: !readOnly,
    editorProps: {
      attributes: {
        name: 'Document editor',
        spellcheck: 'false',
        // Prevent Chrome mobile from offering saved passwords/autofill
        autocomplete: 'cc-csc',
        enterkeyhint: 'done',
        autocorrect: 'off',
        autocapitalize: 'off',
        inputmode: 'text',
        role: 'textbox',
        'aria-autocomplete': 'none',
        'data-ghost-editor': 'true',
        'aria-label': 'Document editor',
        'data-form-type': 'rich-text'
      },
      handleDOMEvents: {
        keydown: (_view, event) => {
          if (isModifiedPasteShortcutEvent(event)) {
            lastModifiedPasteShortcutAtRef.current = Date.now();
          }

          if (!readOnlyRef.current && diffModeRef.current === 'suggest' && event?.key === 'Enter') {
            const state = _view?.state;
            const dispatch = _view?.dispatch;
            const addMarkType = state?.schema?.marks?.diffSuggestionAdd;
            const removeMarkType = state?.schema?.marks?.diffSuggestionRemove;
            const selection = state?.selection;
            if (!state || typeof dispatch !== 'function' || !addMarkType || !selection) {
              return false;
            }

            const from = Number(selection.from || 0);
            const to = Number(selection.to || 0);

            if (rangeIntersectsSuggestionMark(state, from, to, 'diffSuggestionRemove')) {
              event.preventDefault();
              return true;
            }

            if (!shouldHandleStructuralSuggestEnter({ selection })) {
              const now = Date.now();
              const addAdjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionAdd');
              const addAttrs = addAdjacentAttrs
                ? normalizeExistingSuggestionAttrs(addAdjacentAttrs, 'add')
                : createSuggestionMarkAttrs({
                  op: 'add',
                  source: 'user_suggest',
                  authorId: currentUserId,
                  createdAt: now,
                  status: 'pending'
                });
              pendingSuggestListEnterRef.current = { at: now, addAttrs };
              return false;
            }

            event.preventDefault();

            const now = Date.now();
            const addAdjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionAdd');
            const addAttrs = addAdjacentAttrs
              ? normalizeExistingSuggestionAttrs(addAdjacentAttrs, 'add')
              : createSuggestionMarkAttrs({
                op: 'add',
                source: 'user_suggest',
                authorId: currentUserId,
                createdAt: now,
                status: 'pending'
              });

            try {
              let tr = state.tr;

              if (!selection.empty) {
                if (rangeContainsOnlyAddSuggestionText(state, from, to)) {
                  // Keep existing add-suggestion content/placeholder and split after it,
                  // so repeated Enter presses preserve previously added empty lines.
                  tr = tr.setSelection(TextSelection.create(tr.doc, to));
                } else if (removeMarkType) {
                  const removeAdjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionRemove');
                  const removeAttrs = removeAdjacentAttrs
                    ? normalizeExistingSuggestionAttrs(removeAdjacentAttrs, 'remove')
                    : createSuggestionMarkAttrs({
                      op: 'remove',
                      annotationId: addAttrs.annotation_id,
                      batchId: addAttrs.batch_id,
                      source: 'user_suggest',
                      authorId: currentUserId,
                      createdAt: now,
                      status: 'pending'
                    });
                  tr = tr.addMark(from, to, removeMarkType.create(removeAttrs));
                  tr = tr.setSelection(TextSelection.create(tr.doc, to));
                }
              }

              const splitPos = Number(tr.selection.from || 0);
              tr = tr.split(splitPos);

              const markerPos = Number(tr.selection.from || splitPos);
              tr = tr.insertText(EMPTY_LINE_SUGGESTION_CHAR, markerPos, markerPos);
              tr = tr.addMark(markerPos, markerPos + 1, addMarkType.create(addAttrs));
              tr = tr.setSelection(TextSelection.create(tr.doc, markerPos, markerPos + 1));

              suggestTypingBatchRef.current = {
                batchId: String(addAttrs?.batch_id || '').trim() || null,
                annotationId: String(addAttrs?.annotation_id || '').trim() || null,
                lastAt: now,
                lastCursor: markerPos + 1
              };

              dispatch(tr.scrollIntoView());
              return true;
            } catch (err) {
              return false;
            }
          }

          if (!readOnlyRef.current && diffModeRef.current === 'suggest' && (event?.key === 'Backspace' || event?.key === 'Delete')) {
            const state = _view?.state;
            const dispatch = _view?.dispatch;
            const removeMarkType = state?.schema?.marks?.diffSuggestionRemove;
            const selection = state?.selection;

            if (!state || typeof dispatch !== 'function' || !removeMarkType || !selection) {
              return false;
            }

            const isBackwardDelete = event.key === 'Backspace';
            const rangeFrom = Number(selection.from || 0);
            const rangeTo = Number(selection.to || 0);

            const tableSuggestionContext = getTableSuggestionContext(state, rangeFrom, rangeTo);
            if (tableSuggestionContext) {
              event.preventDefault();
              const now = Date.now();
              const adjacentAttrs = findAdjacentSuggestionAttrs(
                state,
                rangeFrom,
                rangeTo,
                'diffSuggestionRemove'
              );
              const attrs = adjacentAttrs
                ? normalizeExistingSuggestionAttrs(adjacentAttrs, 'remove')
                : createSuggestionMarkAttrs({
                  op: 'remove',
                  source: 'user_suggest',
                  authorId: currentUserId,
                  createdAt: now,
                  status: 'pending'
                });

              try {
                let tr = state.tr;

                if (tableSuggestionContext.type === 'table') {
                  tr = clearSuggestionMarksInRange(tr, state, tableSuggestionContext.from, tableSuggestionContext.to);
                  tr = tr.setNodeMarkup(tableSuggestionContext.from, undefined, {
                    ...tr.doc.nodeAt(tableSuggestionContext.from)?.attrs,
                    diffOp: 'remove',
                    diffAnnotationId: attrs.annotation_id,
                    diffBatchId: attrs.batch_id
                  });
                } else if (tableSuggestionContext.type === 'rows') {
                  for (const rowRange of tableSuggestionContext.rowRanges || []) {
                    tr = clearSuggestionMarksInRange(tr, state, rowRange.from, rowRange.to);
                    tr = tr.setNodeMarkup(rowRange.from, undefined, {
                      ...tr.doc.nodeAt(rowRange.from)?.attrs,
                      diffOp: 'remove',
                      diffAnnotationId: attrs.annotation_id,
                      diffBatchId: attrs.batch_id
                    });
                  }
                }

                const safeCursor = Math.max(0, Math.min(rangeFrom, tr.doc.content.size));
                tr.setSelection(TextSelection.create(tr.doc, safeCursor));
                dispatch(tr.scrollIntoView());
                return true;
              } catch (err) {
                return false;
              }
            }

            if (rangeIntersectsSuggestionMark(state, rangeFrom, rangeTo, 'diffSuggestionRemove')) {
              event.preventDefault();
              return true;
            }

            let from = rangeFrom;
            let to = rangeTo;
            let nextCursor = rangeFrom;

            if (selection.empty) {
              if (isBackwardDelete) {
                const cursorPos = Math.max(0, Number(rangeFrom || 0));
                const nextChar = String(state.doc.textBetween(cursorPos, cursorPos + 1, '', '') || '');
                if (
                  isStructuralSuggestionMarkerChar(nextChar)
                  && rangeIntersectsSuggestionMark(state, cursorPos, cursorPos + 1, 'diffSuggestionRemove')
                ) {
                  event.preventDefault();
                  try {
                    const anchorPos = Math.max(0, cursorPos - 1);
                    const tr = state.tr.setSelection(Selection.near(state.doc.resolve(anchorPos), -1));
                    dispatch(tr.scrollIntoView());
                    return true;
                  } catch (err) {
                    return true;
                  }
                }
              }

              if (isBackwardDelete) {
                if (rangeFrom <= 0) return false;
                from = rangeFrom - 1;
                to = rangeFrom;
                nextCursor = from;
              } else {
                if (rangeTo >= state.doc.content.size) return false;
                from = rangeTo;
                to = rangeTo + 1;
                nextCursor = to;
              }
            }

            const listPlaceholderRange = getSingleAddSuggestionPlaceholderRange({ state, selection });
            if (listPlaceholderRange) {
              from = Number(listPlaceholderRange.from || from);
              to = Number(listPlaceholderRange.to || to);
              nextCursor = from;
            }

            const removableListItemRange = getRemovableSuggestedListItemRange({ state, selection });
            if (removableListItemRange) {
              event.preventDefault();
              try {
                const tr = state.tr.delete(removableListItemRange.from, removableListItemRange.to);
                const safeCursor = Math.max(0, Math.min(removableListItemRange.from, tr.doc.content.size));
                tr.setSelection(TextSelection.create(tr.doc, safeCursor));
                dispatch(tr.scrollIntoView());
                return true;
              } catch (err) {
                return false;
              }
            }

            if (shouldBlockBoundaryDeleteOnRemoveSuggestedListItem({ state, selection, isBackwardDelete })) {
              event.preventDefault();
              return true;
            }

            if (shouldHandleStructuralHardBreakDelete({ state, selection, isBackwardDelete })) {
                event.preventDefault();
                const now = Date.now();
                const attrsBase = createSuggestionMarkAttrs({
                  op: 'remove',
                  source: 'user_suggest',
                  authorId: currentUserId,
                  createdAt: now,
                  status: 'pending',
                  markerType: 'hard-break',
                  markerDirection: isBackwardDelete ? 'backspace' : 'delete'
                });
                const attrs = {
                  ...attrsBase,
                  marker_type: 'hard-break',
                  marker_direction: isBackwardDelete ? 'backspace' : 'delete'
                };

                try {
                  const markerBasePos = getStructuralHardBreakMarkerPos({ state, selection, isBackwardDelete });
                  const markerPos = Math.max(0, Math.min(Number(markerBasePos ?? from), Number(state.doc?.content?.size || from)));
                  let tr = state.tr;
                  const existingMarkerText = String(tr.doc.textBetween(markerPos, markerPos + 1, '', '') || '');
                  if (existingMarkerText !== HARD_BREAK_SUGGESTION_CHAR) {
                    tr = tr.insertText(HARD_BREAK_SUGGESTION_CHAR, markerPos, markerPos);
                  }

                  tr = tr.addMark(markerPos, markerPos + 1, removeMarkType.create(attrs));
                  const cursorPos = isBackwardDelete
                    ? Math.max(0, markerPos - 1)
                    : markerPos + 1;
                  if (isBackwardDelete) {
                    tr = tr.setSelection(Selection.near(tr.doc.resolve(cursorPos), -1));
                  } else {
                    tr = tr.setSelection(TextSelection.create(tr.doc, cursorPos));
                  }
                  dispatch(tr.scrollIntoView());
                  return true;
                } catch (err) {
                  return false;
                }
            }

            const parentTextblock = selection?.$from?.parent;
            const parentOffset = Number(selection?.$from?.parentOffset || 0);
            const parentContentSize = Number(parentTextblock?.content?.size || 0);
            const hasNodeBefore = Boolean(selection?.$from?.nodeBefore);
            const hasNodeAfter = Boolean(selection?.$from?.nodeAfter);
            const blockStartPos = typeof selection?.$from?.start === 'function'
              ? Number(selection.$from.start())
              : Number(selection?.from || 0);
            const blockEndPos = typeof selection?.$from?.end === 'function'
              ? Number(selection.$from.end())
              : Number(selection?.from || 0);
            const absolutePos = Number(selection?.$from?.pos ?? selection?.from ?? 0);
            let backwardCrossesBlockBoundary = false;
            let forwardCrossesBlockBoundary = false;
            if (selection?.empty) {
              if (isBackwardDelete && absolutePos > 0) {
                try {
                  const prevPos = Math.max(0, absolutePos - 1);
                  const $prev = state.doc.resolve(prevPos);
                  const prevStart = typeof $prev?.start === 'function' ? Number($prev.start()) : blockStartPos;
                  backwardCrossesBlockBoundary = prevStart !== blockStartPos;
                } catch (err) {
                  backwardCrossesBlockBoundary = false;
                }
              }

              if (!isBackwardDelete && absolutePos < Number(state?.doc?.content?.size || absolutePos)) {
                try {
                  const nextPos = Math.min(Number(state?.doc?.content?.size || absolutePos), absolutePos + 1);
                  const $next = state.doc.resolve(nextPos);
                  const nextStart = typeof $next?.start === 'function' ? Number($next.start()) : blockStartPos;
                  forwardCrossesBlockBoundary = nextStart !== blockStartPos;
                } catch (err) {
                  forwardCrossesBlockBoundary = false;
                }
              }
            }
            const parentStart = typeof selection?.$from?.start === 'function'
              ? Number(selection.$from.start())
              : Number(selection?.from || 0);
            const atBackwardBoundary = isBackwardDelete
              && (parentOffset <= 0 || !hasNodeBefore || absolutePos <= blockStartPos || backwardCrossesBlockBoundary);
            const atForwardBoundary = !isBackwardDelete
              && (parentOffset >= parentContentSize || !hasNodeAfter || absolutePos >= blockEndPos || forwardCrossesBlockBoundary);
            const isStructuralBoundaryDelete = Boolean(selection?.empty)
              && Boolean(parentTextblock?.isTextblock)
              && (atBackwardBoundary || atForwardBoundary);

            if (isStructuralBoundaryDelete) {
              const rowFrom = Math.max(0, parentStart);
              const rowTo = Math.max(rowFrom, parentStart + parentContentSize);

              if (isBackwardDelete && (parentOffset <= 0 || !hasNodeBefore)) {
                // Backspace at row start represents deleting the paragraph break,
                // not deleting the paragraph content below.
                event.preventDefault();

                const now = Date.now();
                const breakPos = Math.max(0, rowFrom);
                const attrsBase = createSuggestionMarkAttrs({
                  op: 'remove',
                  source: 'user_suggest',
                  authorId: currentUserId,
                  createdAt: now,
                  status: 'pending',
                  markerType: 'paragraph-break',
                  markerDirection: 'backspace'
                });
                const attrs = {
                  ...attrsBase,
                  marker_type: 'paragraph-break',
                  marker_direction: 'backspace'
                };

                try {
                  let tr = state.tr;
                  const markerPos = breakPos;

                  const existingMarkerText = String(tr.doc.textBetween(markerPos, markerPos + 1, '', '') || '');
                  if (existingMarkerText !== PARAGRAPH_BREAK_SUGGESTION_CHAR) {
                    tr = tr.insertText(PARAGRAPH_BREAK_SUGGESTION_CHAR, markerPos, markerPos);
                  }

                  tr = tr.addMark(markerPos, markerPos + 1, removeMarkType.create(attrs));
                  // Keep cursor at the original boundary where backspace started.
                  tr = tr.setSelection(TextSelection.create(tr.doc, markerPos));
                  dispatch(tr.scrollIntoView());
                  return true;
                } catch (err) {
                  return false;
                }
              }

              if (!isBackwardDelete && (parentOffset >= parentContentSize || !hasNodeAfter)) {
                // Delete at row end should mark the paragraph break contract,
                // not mark the whole paragraph content below/above.
                event.preventDefault();

                const now = Date.now();
                const breakPos = Math.max(0, blockEndPos);
                const attrsBase = createSuggestionMarkAttrs({
                  op: 'remove',
                  source: 'user_suggest',
                  authorId: currentUserId,
                  createdAt: now,
                  status: 'pending',
                  markerType: 'paragraph-break',
                  markerDirection: 'delete'
                });
                const attrs = {
                  ...attrsBase,
                  marker_type: 'paragraph-break',
                  marker_direction: 'delete'
                };

                try {
                  let tr = state.tr;
                  const markerPos = breakPos;

                  const existingMarkerText = String(tr.doc.textBetween(markerPos, markerPos + 1, '', '') || '');
                  if (existingMarkerText !== PARAGRAPH_BREAK_SUGGESTION_CHAR) {
                    tr = tr.insertText(PARAGRAPH_BREAK_SUGGESTION_CHAR, markerPos, markerPos);
                  }

                  tr = tr.addMark(markerPos, markerPos + 1, removeMarkType.create(attrs));
                  tr = tr.setSelection(TextSelection.create(tr.doc, markerPos));
                  dispatch(tr.scrollIntoView());
                  return true;
                } catch (err) {
                  return false;
                }
              }

              if (rowTo > rowFrom && rangeIntersectsSuggestionMark(state, rowFrom, rowTo, 'diffSuggestionRemove')) {
                event.preventDefault();
                return true;
              }

              if (rowTo > rowFrom || shouldHandleStructuralEmptyDelete({ selection, targetText: '' })) {
                event.preventDefault();

                const now = Date.now();
                const adjacentAttrs = findAdjacentSuggestionAttrs(state, rowFrom, rowTo, 'diffSuggestionRemove');
                const attrs = adjacentAttrs
                  ? normalizeExistingSuggestionAttrs(adjacentAttrs, 'remove')
                  : createSuggestionMarkAttrs({
                    op: 'remove',
                    source: 'user_suggest',
                    authorId: currentUserId,
                    createdAt: now,
                    status: 'pending'
                  });

                try {
                  if (rowTo > rowFrom) {
                    const tr = state.tr.addMark(rowFrom, rowTo, removeMarkType.create(attrs));
                    const safeCursor = Math.max(0, Math.min(Number(selection?.from || rowFrom), tr.doc.content.size));
                    tr.setSelection(TextSelection.create(tr.doc, safeCursor));
                    dispatch(tr.scrollIntoView());
                    return true;
                  }

                  const insertPos = Math.max(0, Math.min(rowFrom, Number(state.doc?.content?.size || rowFrom)));
                  const tr = state.tr.insertText(EMPTY_LINE_SUGGESTION_CHAR, insertPos, insertPos);
                  tr.addMark(insertPos, insertPos + 1, removeMarkType.create(attrs));
                  tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));
                  dispatch(tr.scrollIntoView());
                  return true;
                } catch (err) {
                  return false;
                }
              }
            }

            const targetText = state.doc.textBetween(from, to, '', '');
            if (!targetText) {
              // Guardrail: avoid creating empty remove suggestions for structural-only
              // deletes (for example lone hardBreak/empty block transitions).
              return false;
            }

            if (rangeIntersectsSuggestionMark(state, from, to, 'diffSuggestionRemove')) {
              event.preventDefault();
              return true;
            }

            if (rangeContainsOnlyAddSuggestionText(state, from, to)) {
              event.preventDefault();
              try {
                const listRange = getRemovableSuggestedListItemRangeForContentDeletion({ state, from, to });
                const deleteFrom = Number(listRange?.from || from);
                const deleteTo = Number(listRange?.to || to);
                const tr = state.tr.delete(deleteFrom, deleteTo);
                const safeCursor = Math.max(0, Math.min(nextCursor, tr.doc.content.size));
                tr.setSelection(TextSelection.create(tr.doc, safeCursor));
                dispatch(tr.scrollIntoView());
                return true;
              } catch (err) {
                return false;
              }
            }

            event.preventDefault();

            const now = Date.now();
            const adjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionRemove');
            const attrs = adjacentAttrs
              ? normalizeExistingSuggestionAttrs(adjacentAttrs, 'remove')
              : createSuggestionMarkAttrs({
                op: 'remove',
                source: 'user_suggest',
                authorId: currentUserId,
                createdAt: now,
                status: 'pending'
              });

            suggestDeleteBatchRef.current = {
              batchId: String(attrs?.batch_id || '').trim() || null,
              annotationId: String(attrs?.annotation_id || '').trim() || null,
              lastAt: now,
              lastCursor: nextCursor,
              key: String(event.key || '')
            };

            try {
              const tr = state.tr.addMark(from, to, removeMarkType.create(attrs));
              const safeCursor = Math.max(0, Math.min(nextCursor, tr.doc.content.size));
              tr.setSelection(TextSelection.create(tr.doc, safeCursor));
              dispatch(tr.scrollIntoView());
              return true;
            } catch (err) {
              try {
                const textRanges = getTextOverlapRanges({ state, from, to });
                if (!textRanges.length) return false;

                const removeMark = removeMarkType.create(attrs);
                let tr = state.tr;
                textRanges.forEach((range) => {
                  tr = tr.addMark(range.from, range.to, removeMark);
                });

                const safeCursor = Math.max(0, Math.min(nextCursor, tr.doc.content.size));
                tr = tr.setSelection(TextSelection.create(tr.doc, safeCursor));
                dispatch(tr.scrollIntoView());
                return true;
              } catch (fallbackErr) {
                return false;
              }
            }
          }

          return false;
        },
        paste: (_view, event) => {
          const recentShortcutIntent =
            (Date.now() - lastModifiedPasteShortcutAtRef.current) < MODIFIED_PASTE_SHORTCUT_WINDOW_MS;
          bypassStructuredPasteFormattingRef.current = Boolean(event?.shiftKey) || recentShortcutIntent;
          codePasteContextRef.current = isCodePasteContext(_view);
          return false;
        }
      },
      handleTextInput: (view, from, to, text) => {
        if (readOnlyRef.current || diffModeRef.current !== 'suggest') return false;
        const normalizedText = String(text || '');
        if (!normalizedText) return false;

        const state = view?.state;
        const dispatch = view?.dispatch;
        const addMarkType = state?.schema?.marks?.diffSuggestionAdd;
        if (!state || typeof dispatch !== 'function' || !addMarkType) return false;

        const now = Date.now();
        const hasSelectionRange = Number.isFinite(from) && Number.isFinite(to) && to > from;
        if (rangeIntersectsSuggestionMark(state, from, to, 'diffSuggestionRemove')) {
          return true;
        }
        const selectionIsOnlyAddSuggestion = hasSelectionRange
          ? rangeContainsOnlyAddSuggestionText(state, from, to)
          : false;
        const addAdjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionAdd');
        const addAttrs = addAdjacentAttrs
          ? normalizeExistingSuggestionAttrs(addAdjacentAttrs, 'add')
          : createSuggestionMarkAttrs({
            op: 'add',
            source: 'user_suggest',
            authorId: currentUserId,
            createdAt: now,
            status: 'pending'
          });

        suggestTypingBatchRef.current = {
          batchId: String(addAttrs?.batch_id || '').trim() || null,
          annotationId: String(addAttrs?.annotation_id || '').trim() || null,
          lastAt: now,
          lastCursor: null
        };

        try {
          const tr = state.tr;

          if (hasSelectionRange) {
            let addFrom = to;
            let addTo = to + normalizedText.length;

            if (selectionIsOnlyAddSuggestion) {
              tr.insertText(normalizedText, from, to);
              addFrom = from;
              addTo = from + normalizedText.length;
            } else {
              const removeMarkType = state.schema?.marks?.diffSuggestionRemove;
              if (removeMarkType) {
                const removeAdjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionRemove');
                const removeAttrs = removeAdjacentAttrs
                  ? normalizeExistingSuggestionAttrs(removeAdjacentAttrs, 'remove')
                  : createSuggestionMarkAttrs({
                    op: 'remove',
                    annotationId: addAttrs.annotation_id,
                    batchId: addAttrs.batch_id,
                    source: 'user_suggest',
                    authorId: currentUserId,
                    createdAt: now,
                    status: 'pending'
                  });
                tr.addMark(from, to, removeMarkType.create(removeAttrs));
              }

              tr.insertText(normalizedText, to, to);
            }

            tr.addMark(addFrom, addTo, addMarkType.create(addAttrs));
            tr.setSelection(TextSelection.create(tr.doc, addTo));
            suggestTypingBatchRef.current.lastCursor = addTo;
          } else {
            tr.insertText(normalizedText, from, to);
            tr.addMark(from, from + normalizedText.length, addMarkType.create(addAttrs));
            suggestTypingBatchRef.current.lastCursor = from + normalizedText.length;
          }

          dispatch(tr.scrollIntoView());
          notifyUserInteraction({ key: null, ctrlKey: false, metaKey: false, altKey: false }, 'keydown');
          return true;
        } catch (err) {
          return false;
        }
      },
      transformPasted: (slice) => {
        // Clean pasted content by removing chunk attributes
        return cleanSlice(slice);
      },
      transformPastedHTML: (html) => {
        if (bypassStructuredPasteFormattingRef.current || codePasteContextRef.current) {
          return formatHelpers.sanitizePastedHtml(html);
        }
        return formatHelpers.formatPastedHtml(html);
      },
      transformPastedText: (text) => formatHelpers.formatPastedText(text),
      handlePaste: (view, event, slice) => {
        const isSuggestMode = diffModeRef.current === 'suggest' && !readOnlyRef.current;
        const bypassStructuredFormatting = bypassStructuredPasteFormattingRef.current;
        const isCodeContextPaste = codePasteContextRef.current;
        bypassStructuredPasteFormattingRef.current = false;
        codePasteContextRef.current = false;
        lastModifiedPasteShortcutAtRef.current = 0;

        // Modified paste (Shift+Paste / Ctrl+Shift+V) is an explicit opt-out:
        // skip custom rich formatting in write mode. In suggest mode we still
        // process paste so inserted content is tracked as suggestions.
        if (shouldUseNativePasteBypass({ bypassStructuredFormatting, isSuggestMode })) {
           return false;
        }

        const clipHtmlRaw = event.clipboardData?.getData('text/html') || '';
        const clipText = event.clipboardData?.getData('text/plain') || '';
        const clipHtml = bypassStructuredFormatting ? '' : clipHtmlRaw;

        const insertSuggestTrackedContent = ({ html = '', text = '' } = {}) => {
          const state = view?.state;
          const dispatch = view?.dispatch;
          const selection = state?.selection;
          const addMarkType = state?.schema?.marks?.diffSuggestionAdd;
          const removeMarkType = state?.schema?.marks?.diffSuggestionRemove;
          if (!state || typeof dispatch !== 'function' || !selection || !addMarkType) return false;

          const from = Number(selection.from || 0);
          const to = Number(selection.to || 0);

          if (rangeIntersectsSuggestionMark(state, from, to, 'diffSuggestionRemove')) {
            return true;
          }

          const beforeDoc = state.doc;

          const hasSelectionRange = to > from;
          const selectionIsOnlyAddSuggestion = hasSelectionRange
            ? rangeContainsOnlyAddSuggestionText(state, from, to)
            : false;

          const now = Date.now();
          const addAdjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionAdd');
          const addAttrs = addAdjacentAttrs
            ? normalizeExistingSuggestionAttrs(addAdjacentAttrs, 'add')
            : createSuggestionMarkAttrs({
              op: 'add',
              source: 'user_suggest',
              authorId: currentUserId,
              createdAt: now,
              status: 'pending'
            });

          let parsedSlice = null;
          if (html) {
            try {
              const schema = state.schema;
              const host = document.createElement('div');
              host.innerHTML = html;
              const parser = PMDOMParser.fromSchema(schema);
              parsedSlice = parser.parseSlice(host, { preserveWhitespace: true });
            } catch (err) {
              parsedSlice = null;
            }
          }

          let tr = state.tr;
          let addFrom = from;

          if (hasSelectionRange) {
            if (selectionIsOnlyAddSuggestion) {
              tr.setSelection(TextSelection.create(tr.doc, from, to));
              addFrom = from;
            } else {
              if (removeMarkType) {
                const removeAdjacentAttrs = findAdjacentSuggestionAttrs(state, from, to, 'diffSuggestionRemove');
                const removeAttrs = removeAdjacentAttrs
                  ? normalizeExistingSuggestionAttrs(removeAdjacentAttrs, 'remove')
                  : createSuggestionMarkAttrs({
                    op: 'remove',
                    annotationId: addAttrs.annotation_id,
                    batchId: addAttrs.batch_id,
                    source: 'user_suggest',
                    authorId: currentUserId,
                    createdAt: now,
                    status: 'pending'
                  });
                tr.addMark(from, to, removeMarkType.create(removeAttrs));
              }
              tr.setSelection(TextSelection.create(tr.doc, to));
              addFrom = to;
            }
          } else {
            tr.setSelection(TextSelection.create(tr.doc, from));
            addFrom = from;
          }

          if (parsedSlice) {
            // When pasting multiple blocks over a selection in suggest mode, force a
            // closed slice (openStart/openEnd = 0) so each pasted block is inserted as
            // a standalone node rather than merging its first block into the node the
            // cursor sits in after marking the removed range.
            const shouldForceClose = hasSelectionRange && !selectionIsOnlyAddSuggestion
              && parsedSlice.openStart > 0 && parsedSlice.content.childCount > 1;
            if (shouldForceClose) {
              // Also reposition the cursor to AFTER the depth-1 ancestor (the
              // top-level block containing `to`). This prevents multi-block pastes
              // from being inserted inside a list item or other nested container.
              try {
                const $toPos = tr.doc.resolve(to);
                if ($toPos.depth > 1) {
                  const afterTopLevel = $toPos.after(1);
                  tr.setSelection(TextSelection.create(tr.doc, afterTopLevel));
                  addFrom = afterTopLevel;
                }
              } catch (e) {}
            }
            const insertSlice = shouldForceClose
              ? new Slice(parsedSlice.content, 0, 0)
              : parsedSlice;
            tr.replaceSelection(insertSlice);
          } else {
            let normalizedText = getFallbackPasteText({ rawText: text, html });
            if (!normalizedText) return false;
            tr.insertText(normalizedText, tr.selection.from, tr.selection.to);
          }

          if (!tr.docChanged) {
            let fallbackText = getFallbackPasteText({ rawText: text, html });
            if (!fallbackText) return false;
            tr.insertText(fallbackText, tr.selection.from, tr.selection.to);
          }

          if (!tr.docChanged) return false;

          const addTo = Number(tr.selection.from || addFrom);
          if (addTo > addFrom) {
            try {
              tr.addMark(addFrom, addTo, addMarkType.create(addAttrs));
            } catch (err) {
              // Ignore block-only paste ranges where add mark cannot be applied.
            }
          }

          tr.setSelection(TextSelection.create(tr.doc, addTo));
          suggestTypingBatchRef.current = {
            batchId: String(addAttrs?.batch_id || '').trim() || null,
            annotationId: String(addAttrs?.annotation_id || '').trim() || null,
            lastAt: now,
            lastCursor: addTo
          };

          dispatch(tr.scrollIntoView());
          if (beforeDoc && view?.state?.doc && typeof view.state.doc.eq === 'function' && view.state.doc.eq(beforeDoc)) {
            return false;
          }
          notifyUserInteraction(event, 'paste');
          return true;
        };

        // Helper: only report handled when insertion is confirmed.
        // If insertion cannot be confirmed, return false so default paste runs.
        const didDocChange = (beforeDoc) => {
          try {
            const afterDoc = view?.state?.doc;
            if (!beforeDoc || !afterDoc) return false;
            return !afterDoc.eq(beforeDoc);
          } catch {
            return false;
          }
        };

        const insertWithFallback = (html, rawText, options = {}) => {
          const { showConversionToast = false } = options;
          const cmds = editorCommandRef.current;
          if (!cmds && !view?.dispatch) return false;

          if (isSuggestMode) {
            return insertSuggestTrackedContent({ html, text: rawText });
          }

          const beforeDoc = view?.state?.doc || null;
          let inserted = false;
          let insertedViaHtml = false;
          let changedViaHtml = false;

          if (html) {
            try {
              const schema = view?.state?.schema;
              if (!schema) throw new Error('missing schema');
              const host = document.createElement('div');
              host.innerHTML = html;
              const parser = PMDOMParser.fromSchema(schema);
              const parsed = parser.parseSlice(host, {
                preserveWhitespace: true
              });
              const tr = view.state.tr.replaceSelection(parsed).scrollIntoView();
              view.dispatch(tr);
              changedViaHtml = beforeDoc ? didDocChange(beforeDoc) : true;
              inserted = changedViaHtml;
              insertedViaHtml = inserted;
            } catch {
              inserted = false;
            }
          }

          let fallbackText = getFallbackPasteText({ rawText, html });

          if (!inserted && fallbackText) {
            try {
              cmds?.focus?.();
              inserted = cmds?.insertText?.(fallbackText) !== false;
              if (inserted && beforeDoc) {
                inserted = didDocChange(beforeDoc);
              }
            } catch {
              inserted = false;
            }
          }

          // Final hard fallback: direct ProseMirror transaction insert.
          if (!inserted && fallbackText && view?.state && view?.dispatch) {
            try {
              const tr = view.state.tr.insertText(fallbackText);
              view.dispatch(tr.scrollIntoView());
              inserted = beforeDoc ? didDocChange(beforeDoc) : true;
            } catch {
              inserted = false;
            }
          }

          if (!inserted) return false;

          if (showConversionToast && insertedViaHtml && changedViaHtml) {
            notifyToast({
              message: 'Auto-formatted',
              level: 'info',
              timeoutMs: 2000,
              where: {
                pane,
                docId,
                scope: 'doc'
              }
            });
          }
          notifyUserInteraction(event, 'paste');
          return true;
        };

        // HTML clipboard path.
        if (!isCodeContextPaste && clipHtml) {
          const html = formatHelpers.formatPastedHtml(clipHtml);
          if (html) {
            // Content was meaningfully reformatted if the output differs from
            // a bare sanitization pass (i.e. structural changes were made).
            const sanitizedOnly = formatHelpers.sanitizePastedHtml(clipHtml);
            const showConversionToast = html !== sanitizedOnly;
            const handled = insertWithFallback(html, clipText, { showConversionToast });
            if (handled) return true;
          }
          // formatPastedHtml returned empty — fall through to text path.
        }

        // Plain-text clipboard path. Convert to rich HTML where possible,
        // with a final catch-all so something always inserts.
        if (!isCodeContextPaste && clipText) {
          if (formatHelpers.isEntireHtmlContent(clipText)) {
            const html = formatHelpers.formatPastedHtml(clipText);
            if (html) {
              const handled = insertWithFallback(html, clipText, { showConversionToast: true });
              if (handled) return true;
            }
          }

          if (formatHelpers.hasHtmlLikeMarkup(clipText)) {
            const html = formatHelpers.convertMixedHtmlSnippetsToRichHtml(clipText);
            if (html) {
              const handled = insertWithFallback(html, clipText, { showConversionToast: true });
              if (handled) return true;
            }
          }

          if (formatHelpers.hasMarkdownStructure(clipText) || formatHelpers.hasTitleStructure(clipText)) {
            const html = formatHelpers.markdownTextToHtml(clipText);
            if (html) {
              const handled = insertWithFallback(html, clipText, { showConversionToast: true });
              if (handled) return true;
            }
          }

          // Final catch-all: always deliver the text. Try markdown conversion
          // first (wraps in <p> tags preserving paragraphs), fall back to raw
          // text insertion so Ctrl+V never silently fails.
          const html = formatHelpers.markdownTextToHtml(clipText);
          const hasStructure = html && /<(h[1-6]|ul|ol|li)[\s>]/i.test(html);
          const handled = insertWithFallback(html || null, clipText, { showConversionToast: Boolean(hasStructure) });
          if (handled) return true;
        }

        const handled = trySmartBoundaryPaste(view, slice);
        if (handled) {
          notifyUserInteraction(event, 'paste');
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      // Guard against undefined editor
      if (!editor) return;

      if (!readOnlyRef.current && diffModeRef.current === 'suggest') {
        const pendingListEnter = pendingSuggestListEnterRef.current;
        pendingSuggestListEnterRef.current = null;
        if (pendingListEnter && Date.now() - Number(pendingListEnter?.at || 0) < 1400) {
          try {
            const state = editor.state;
            const selection = state?.selection;
            const addMarkType = state?.schema?.marks?.diffSuggestionAdd;
            const from = Number(selection?.from || 0);
            const to = Number(selection?.to || 0);
            if (
              addMarkType
              && shouldInjectListItemSuggestionMarker({ selection })
              && !rangeIntersectsSuggestionMark(state, from, to, 'diffSuggestionRemove')
            ) {
              let tr = state.tr.insertText(EMPTY_LINE_SUGGESTION_CHAR, from, to);
              tr = tr.addMark(from, from + 1, addMarkType.create(pendingListEnter.addAttrs));
              tr = tr.setSelection(TextSelection.create(tr.doc, from, from + 1));
              suggestTypingBatchRef.current = {
                batchId: String(pendingListEnter?.addAttrs?.batch_id || '').trim() || null,
                annotationId: String(pendingListEnter?.addAttrs?.annotation_id || '').trim() || null,
                lastAt: Date.now(),
                lastCursor: from + 1
              };
              editor.view.dispatch(tr.scrollIntoView());
              return;
            }
          } catch (err) {
            // Ignore marker injection failures; native list split already applied.
          }
        }
      }
      
      // Skip if this is a system-triggered update
      if (isSystemUpdate.current) return;
      
      // Clear previous timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Set new timeout for debounced save
      saveTimeoutRef.current = setTimeout(() => {
        // Guard against undefined editor after timeout
        if (!editor) return;
        
        const json = editor.getJSON();
        const html = editor.getHTML();
        debouncedOnChange({ json, html });
      }, SAVE_TIMEOUT_MS);
    },
    onBlur: () => {
      if (onBlur) onBlur();
    },
  });

  // Track the last time the user pressed the mouse inside the editor area so
  // the window-keydown listener below can tell whether the intent was to type
  // into this particular editor (contenteditable=false won't focus the element
  // on click, so we can't rely on document.activeElement checks).
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onMouseDown = () => {
      if (readOnlyRef.current) lastEditorClickRef.current = Date.now();
    };
    el.addEventListener('mousedown', onMouseDown);
    return () => el.removeEventListener('mousedown', onMouseDown);
  }, [editor]); // re-run once editor is created so rootRef.current is populated

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const onKeyDown = (event) => notifyUserInteraction(event, 'keydown');
    const onBeforeInput = (event) => notifyUserInteraction(event, 'beforeinput');
    const onClick = (event) => notifyUserInteraction(event, 'click');
    const onPaste = (event) => notifyUserInteraction(event, 'paste');
    const onDrop = (event) => notifyUserInteraction(event, 'drop');
    const onCompositionStart = (event) => notifyUserInteraction(event, 'compositionstart');

    // Capture typical user edit-intent events so save paths can audit user-triggered edits.
    el.addEventListener('keydown', onKeyDown, true);
    el.addEventListener('beforeinput', onBeforeInput, true);
    el.addEventListener('click', onClick, true);
    el.addEventListener('paste', onPaste, true);
    el.addEventListener('drop', onDrop, true);
    el.addEventListener('compositionstart', onCompositionStart, true);

    return () => {
      try { el.removeEventListener('keydown', onKeyDown, true); } catch (e) {}
      try { el.removeEventListener('beforeinput', onBeforeInput, true); } catch (e) {}
      try { el.removeEventListener('click', onClick, true); } catch (e) {}
      try { el.removeEventListener('paste', onPaste, true); } catch (e) {}
      try { el.removeEventListener('drop', onDrop, true); } catch (e) {}
      try { el.removeEventListener('compositionstart', onCompositionStart, true); } catch (e) {}
    };
  }, [editor, notifyUserInteraction]);

  const [linkMenu, setLinkMenu] = useState({ open: false, url: '', target: '', x: 0, y: 0, anchorPos: null, anchorEl: null, editing: false });

  // Intercept anchor clicks inside the editor and show link menu
  useEffect(() => {
    if (!editor || !rootRef.current) return;
    const el = rootRef.current;
    const getAnchor = (eventTarget) => eventTarget && eventTarget.closest && eventTarget.closest('a[href]');

    const preventNativeAnchorNavigation = (e) => {
      try {
        const a = getAnchor(e.target);
        if (!a) return;
        if (!el.contains(a)) return;
        e.preventDefault();
        e.stopPropagation();
      } catch (err) {}
    };

    const onClick = (e) => {
      try {
        const a = getAnchor(e.target);
        if (!a) return;
        if (!el.contains(a)) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = a.getBoundingClientRect();
        const x = rect.left + window.scrollX;
        const y = rect.bottom + window.scrollY + 6;
        const pos = Number.isFinite(editor?.view?.posAtDOM?.(a, 0)) ? editor.view.posAtDOM(a, 0) : null;
        setLinkMenu((prev) => {
          if (prev.open && prev.editing) return prev;
          return { open: true, url: a.getAttribute('href') || '', target: a.getAttribute('target') || '', x, y, anchorPos: pos, anchorEl: a, editing: false };
        });
      } catch (err) {
        // ignore
      }
    };
    const onMouseOver = (e) => {
      try {
        const a = getAnchor(e.target);
        if (!a) return;
        if (!el.contains(a)) return;
        const rect = a.getBoundingClientRect();
        const x = rect.left + window.scrollX;
        const y = rect.bottom + window.scrollY + 6;
        const pos = Number.isFinite(editor?.view?.posAtDOM?.(a, 0)) ? editor.view.posAtDOM(a, 0) : null;
        setLinkMenu((prev) => {
          if (prev.open && prev.editing) return prev;
          return { open: true, url: a.getAttribute('href') || '', target: a.getAttribute('target') || '', x, y, anchorPos: pos, anchorEl: a, editing: false };
        });
      } catch (err) {}
    };
    el.addEventListener('pointerdown', preventNativeAnchorNavigation, true);
    el.addEventListener('click', preventNativeAnchorNavigation, true);
    el.addEventListener('click', onClick);
    el.addEventListener('mouseover', onMouseOver);
    return () => {
      try { el.removeEventListener('pointerdown', preventNativeAnchorNavigation, true); } catch (e) {}
      try { el.removeEventListener('click', preventNativeAnchorNavigation, true); } catch (e) {}
      try { el.removeEventListener('click', onClick); } catch (e) {}
      try { el.removeEventListener('mouseover', onMouseOver); } catch (e) {}
    };
  }, [editor, rootRef]);

  // When the editor is read-only, listen for keydown at the window level.
  // Only show the "view only" toast if the user recently clicked inside the
  // editor area (within EDITOR_CLICK_WINDOW_MS) and pressed a content key.
  useEffect(() => {
    if (!readOnly) return;

    const IGNORED_KEYS = [
      'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab',
      'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'PageUp', 'PageDown',
      'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
    ];

    const onKeyDown = (e) => {
      if (!readOnlyRef.current) return;
      // Only fire if the user recently clicked inside the editor
      if (Date.now() - lastEditorClickRef.current > EDITOR_CLICK_WINDOW_MS) return;
      // Ignore Ctrl/Cmd combos (copy, select-all, find, etc.)
      if (e.ctrlKey || e.metaKey) return;
      // Ignore pure modifier/navigation/function keys
      if (IGNORED_KEYS.includes(e.key)) return;

      const now = Date.now();
      if (now - viewOnlyToastThrottleRef.current > VIEW_ONLY_TOAST_INTERVAL_MS) {
        viewOnlyToastThrottleRef.current = now;
        notifyToast({
          message: 'This document is view only',
          where: {
            pane,
            docId,
            scope: 'doc'
          }
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [docId, pane, readOnly]);

  useEffect(() => {
    if (htmlMode) return;
    if (!editor || !rootRef.current) return;

    const target = rootRef.current;

    // Use TipTap's setOptions mounting API instead of manually moving DOM nodes.
    // This mirrors what `EditorContent` does internally and lets TipTap
    // initialize its content/component lifecycle reliably across platforms.
    try {
      // If the editor DOM is already created and attached to some temporary
      // parent, move its child nodes into our target container first. This
      // mirrors what `EditorContent` does and ensures the visible DOM is
      // transferred correctly (important for some browsers/mobile timing).
      try {
        const proseParent = editor.view && editor.view.dom && editor.view.dom.parentNode;
        if (proseParent && proseParent !== target) {
          // Move all child nodes into our target
          target.append(...Array.from(proseParent.childNodes));
        }
      } catch (e) {
        // ignore
      }

      editor.setOptions({ element: target });
      editorCommandRef.current = editor.commands;
      if (setEditorInstance) setEditorInstance(editor);
    } catch (err) {
      // ignore mounting failures
    }

    return () => {
      try {
        // Detach the editor from our element by giving it a fresh container
        const newElement = document.createElement('div');
        editor.setOptions({ element: newElement });
      } catch (err) {}
      try { if (setEditorInstance) setEditorInstance(null); } catch (e) {}
    };
  }, [editor, setEditorInstance, htmlMode]);

  // Sync external content into the editor without wiping history
  useEffect(() => {
    if (editor && content) {
      // Don't update content if editor has focus to avoid disrupting typing
      if (editor.isFocused) {
        return;
      }
      
      // Extract JSON from wrapper if needed
      let contentToSet = content;
      
      // Check if content is our wrapper format { json, html }
      if (content && typeof content === 'object' && content.json) {
        contentToSet = content.json;
      }
      // Otherwise it's a legacy HTML string or already JSON - pass through
      
      // Get current content from editor
      const currentContent = editor.getJSON();
      
      // Only update if different
      if (JSON.stringify(contentToSet) !== JSON.stringify(currentContent)) {
        isSystemUpdate.current = true;
        editor.commands.setContent(contentToSet, false);
        setTimeout(() => {
          isSystemUpdate.current = false;
        }, 100);
      }
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;
    if (typeof editor.setEditable === 'function') {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  if (!editor) return null;

  if (htmlMode) {
    return (
      <pre className="htmlView">
        {content}
      </pre>
    );
  }

  return (
    <>
      <div ref={rootRef} className="document" />
      {linkMenu.open && (
        <PopoverLinks
            editor={editor}
            url={linkMenu.url}
            linkTarget={linkMenu.target}
            position={{ x: linkMenu.x, y: linkMenu.y }}
            anchorPos={linkMenu.anchorPos}
            triggerEl={linkMenu.anchorEl}
            canEdit={!readOnly}
            editing={!!linkMenu.editing}
            onEditingChange={(nextEditing) => {
              setLinkMenu((prev) => {
                if (!prev.open || prev.editing === !!nextEditing) return prev;
                return { ...prev, editing: !!nextEditing };
              });
            }}
          projectId={projectId}
          itemsMap={itemsMap}
          onOpenProjectDoc={onOpenProjectDoc}
            onClose={() => setLinkMenu({ open: false, url: '', target: '', x: 0, y: 0, anchorPos: null, anchorEl: null, editing: false })}
        />
      )}
    </>
  );
}